import Box from '@components/base/Box'
import { Button } from '@components/inputs/Button'
import { TextInput } from '@components/inputs/TextInput'
import { TicketPurchaseInput } from '@graphql/__generated/graphql'
import { ReactNode } from 'react'
import { MdOutlineQuiz } from 'react-icons/md'

type AdditionalAttendeesStepProps = {
  activeAdditionalTickets: TicketPurchaseInput[]
  attendeesError: string
  disableFormProgress: boolean
  errors: {
    step: string
    message: string | ReactNode
    eleId?: string
    alert?: boolean
  }[]
  errorHandler: (
    id: string,
    alert?: boolean
  ) => (message: string, testId: string) => void
  getEmail: () => string | null
  labelBgColour?: string
  onBack: () => void
  onContinue: () => void
  onTicketChange: (
    index: number,
    ticket: Partial<TicketPurchaseInput>
  ) => void
  saving: boolean
  setAttendeesError: (message: string) => void
  textColour?: string
}

const AdditionalAttendeesStep = ({
  activeAdditionalTickets,
  attendeesError,
  disableFormProgress,
  errors,
  errorHandler,
  getEmail,
  labelBgColour,
  onBack,
  onContinue,
  onTicketChange,
  saving,
  setAttendeesError,
  textColour,
}: AdditionalAttendeesStepProps) => {
  return (
    <>
      <Box className="mb-2 text-lg">
        <MdOutlineQuiz className="inline-block w-8 h-8 mr-1" /> Attendee
        Information
      </Box>
      {activeAdditionalTickets?.map((attendee, index) => {
        return (
          <Box
            key={`additional_ticket_info_${index + 1}`}
            className={`mb-2`}
          >
            <Box className="mt-1 mb-2">{`Additional Attendee ${
              index + 1
            }`}</Box>
            <TextInput
              key={index}
              validations={{
                minLength: {
                  value: 1,
                  message: 'Field name must be at least 1 character',
                },
                maxLength: {
                  value: 70,
                  message: 'Field name must be at most 70 characters',
                },
              }}
              required
              value={attendee.fullName}
              onChange={(value) =>
                onTicketChange(index, {
                  fullName: value,
                })
              }
              label={`Full Name`}
              placeholder={`Enter additional attendee ${
                index + 1
              }'s full name`}
            />

            <TextInput
              validations={{
                isUnique: {
                  value: [
                    getEmail(),
                    ...activeAdditionalTickets
                      .map((additionalAttendee, additionalAttendeeIndex) =>
                        additionalAttendeeIndex === index
                          ? ''
                          : additionalAttendee.email
                      )
                      .filter((email) => !!email && email !== ''),
                  ],
                  message: 'You must provide a unique email for each ticket',
                },
                minLength: {
                  value: 7,
                  message: 'Email must be at least 7 characters',
                },
                maxLength: {
                  value: 70,
                  message: 'Email must be less than 70 characters',
                },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email must be valid',
                },
              }}
              setError={errorHandler('Attendee Information')}
              error={
                errors.find(
                  (error) =>
                    error.eleId === `additional_attendee_email_${index}`
                )?.message
              }
              onChange={(value) =>
                onTicketChange(index, {
                  email: value,
                })
              }
              required
              value={attendee.email}
              type="email"
              label={`Email`}
              testid={`additional_attendee_email_${index}`}
              placeholder={`Enter additional attendee ${index + 1}'s email`}
              textColour={textColour}
              labelBgColour={labelBgColour}
            />
          </Box>
        )
      })}
      <Box ignoreTheme className="mt-4 text-center text-red-500 text-xs">
        {attendeesError}
      </Box>
      <Box className="flex w-full mt-4">
        <Button
          title="Back"
          className="flex-2 mr-2"
          buttonType="submit"
          onClick={onBack}
        />
        <Button
          disabled={disableFormProgress}
          title="Continue"
          className="flex-[8] !text-white"
          textColor={!disableFormProgress ? 'white' : undefined}
          buttonType="submit"
          onClick={() => {
            setAttendeesError('')
            onContinue()
          }}
          loading={saving}
        />
      </Box>
    </>
  )
}

export default AdditionalAttendeesStep
